import React, { useState, useEffect } from "react";
import {
  VStack,
  FormControl,
  FormLabel,
  Input,
  Button,
  FormErrorMessage,
  Box,
  Text,
  Checkbox,
  SimpleGrid,
  Divider,
  useColorModeValue,
  Badge,
  HStack,
  Alert,
  AlertIcon,
  Spinner,
  Center,
} from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import type { Role, Permission } from "../../types";
import { getPermissionKeys } from "../../utils/permissions";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4001/v1";

// Group permissions by resource
const groupPermissionsByResource = (permissions: Permission[]) => {
  return permissions.reduce(
    (acc, permission) => {
      if (!acc[permission.resource]) {
        acc[permission.resource] = [];
      }
      acc[permission.resource].push(permission);
      return acc;
    },
    {} as Record<string, Permission[]>,
  );
};

interface RoleFormProps {
  initialData?: Role;
  onSubmit: (data: Partial<Role>) => void;
  onCancel: () => void;
}

export const RoleForm: React.FC<RoleFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
}) => {
  const initialPermissionKeys = getPermissionKeys(initialData?.permissions);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<Role>({
    defaultValues: {
      label: initialData?.label || "",
      value: initialData?.value || "",
      permissions: initialPermissionKeys,
      isSuperAdmin: initialData?.isSuperAdmin || false,
    } as any,
  });

  const [selectedPermissions, setSelectedPermissions] = useState<string[]>(
    initialPermissionKeys,
  );

  // Fetch permissions from API
  const { data: permissions = [], isLoading: isLoadingPermissions } = useQuery({
    queryKey: ["permissions"],
    queryFn: async () => {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/permissions`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data.data;
    },
  });

  const isSuperAdmin = watch("isSuperAdmin");
  const groupedPermissions = groupPermissionsByResource(permissions);

  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  // Update form value when permissions change
  useEffect(() => {
    setValue("permissions", selectedPermissions as any);
  }, [selectedPermissions, setValue]);

  // Handle super admin toggle
  useEffect(() => {
    if (isSuperAdmin) {
      // Super admin gets all permissions
      const allPermissions = permissions.map((p: Permission) => p.key);
      setSelectedPermissions(allPermissions);
    }
  }, [isSuperAdmin, permissions]);

  const handlePermissionToggle = (permission: string) => {
    if (isSuperAdmin) return; // Can't modify super admin permissions manually

    setSelectedPermissions((prev) =>
      prev.includes(permission)
        ? prev.filter((p) => p !== permission)
        : [...prev, permission],
    );
  };

  const handleResourceToggle = (resource: string) => {
    if (isSuperAdmin) return; // Can't modify super admin permissions manually

    const resourcePermissions = groupedPermissions[resource].map(
      (p: Permission) => p.key,
    );
    const allSelected = resourcePermissions.every((p: string) =>
      selectedPermissions.includes(p),
    );

    if (allSelected) {
      // Deselect all in resource
      setSelectedPermissions((prev) =>
        prev.filter((p) => !resourcePermissions.includes(p)),
      );
    } else {
      // Select all in resource
      setSelectedPermissions((prev) => [
        ...prev,
        ...resourcePermissions.filter((p: string) => !prev.includes(p)),
      ]);
    }
  };

  const handleSelectAll = () => {
    if (isSuperAdmin) return;

    const allPermissions = permissions.map((p: Permission) => p.key);
    if (selectedPermissions.length === allPermissions.length) {
      setSelectedPermissions([]);
    } else {
      setSelectedPermissions(allPermissions);
    }
  };
  const onFormSubmit = (data: any) => {
    onSubmit({
      ...data,
      permissions: selectedPermissions,
    });
  };

  const isReadOnly = initialData?.isSuperAdmin === true;

  if (isLoadingPermissions) {
    return (
      <Center py={8}>
        <Spinner size="xl" />
      </Center>
    );
  }

  return (
    <form onSubmit={handleSubmit(onFormSubmit)}>
      <VStack spacing={6} align="stretch">
        {isReadOnly && (
          <Alert status="warning" borderRadius="md">
            <AlertIcon />
            این نقش ویژه سوپر ادمین است و قابل ویرایش نیست.
          </Alert>
        )}

        <FormControl isInvalid={!!errors.label} isRequired>
          <FormLabel>عنوان نقش</FormLabel>
          <Input
            {...register("label", { required: "عنوان الزامی است" })}
            placeholder="مثال: مدیر محتوا"
            isDisabled={isReadOnly}
          />
          <FormErrorMessage>{errors.label?.message as string}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors.value} isRequired>
          <FormLabel>مقدار (نام سیستمی)</FormLabel>
          <Input
            {...register("value", { required: "مقدار الزامی است" })}
            placeholder="مثال: content_manager"
            isDisabled={isReadOnly || !!initialData}
          />
          <FormErrorMessage>{errors.value?.message as string}</FormErrorMessage>
        </FormControl>

        <Divider />

        {/* Permissions Section */}
        <Box>
          <HStack justifyContent="space-between" mb={4}>
            <Text fontSize="lg" fontWeight="bold">
              مجوزهای دسترسی
            </Text>
            {!isSuperAdmin && (
              <Button size="sm" variant="outline" onClick={handleSelectAll}>
                {selectedPermissions.length === permissions.length
                  ? "لغو انتخاب همه"
                  : "انتخاب همه"}
              </Button>
            )}
          </HStack>

          {isSuperAdmin && (
            <Alert status="info" mb={4} borderRadius="md">
              <AlertIcon />
              سوپر ادمین به طور خودکار به همه مجوزها دسترسی دارد.
            </Alert>
          )}

          <VStack spacing={4} align="stretch">
            {Object.entries(groupedPermissions).map(
              ([resource, resourcePerms]) => {
                const resourcePermissions = resourcePerms.map(
                  (p: Permission) => p.key,
                );
                const allResourceSelected = resourcePermissions.every(
                  (p: string) => selectedPermissions.includes(p),
                );
                const someResourceSelected = resourcePermissions.some(
                  (p: string) => selectedPermissions.includes(p),
                );

                return (
                  <Box
                    key={resource}
                    p={4}
                    borderWidth="1px"
                    borderRadius="md"
                    bg={cardBg}
                    borderColor={borderColor}
                  >
                    <HStack justifyContent="space-between" mb={3}>
                      <HStack>
                        <Checkbox
                          isChecked={allResourceSelected}
                          isIndeterminate={
                            someResourceSelected && !allResourceSelected
                          }
                          onChange={() => handleResourceToggle(resource)}
                          colorScheme="brand"
                          isDisabled={isSuperAdmin}
                        >
                          <Text fontWeight="semibold">
                            {resourcePerms[0]?.resourceLabel || resource}
                          </Text>
                        </Checkbox>
                      </HStack>
                      <Badge
                        colorScheme={allResourceSelected ? "green" : "gray"}
                      >
                        {
                          resourcePerms.filter((p: Permission) =>
                            selectedPermissions.includes(p.key),
                          ).length
                        }{" "}
                        / {resourcePerms.length}
                      </Badge>
                    </HStack>

                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2} pr={6}>
                      {resourcePerms.map((permission: Permission) => (
                        <Checkbox
                          key={permission.key}
                          isChecked={selectedPermissions.includes(
                            permission.key,
                          )}
                          onChange={() =>
                            handlePermissionToggle(permission.key)
                          }
                          colorScheme="brand"
                          size="sm"
                          isDisabled={isSuperAdmin}
                        >
                          <HStack spacing={1}>
                            <Text fontSize="sm">{permission.label}</Text>
                            {permission.isDangerous && (
                              <Badge colorScheme="red" fontSize="xs">
                                خطرناک
                              </Badge>
                            )}
                          </HStack>
                        </Checkbox>
                      ))}
                    </SimpleGrid>
                  </Box>
                );
              },
            )}
          </VStack>
        </Box>

        <HStack spacing={3} pt={4}>
          <Button
            type="submit"
            colorScheme="brand"
            isDisabled={isReadOnly}
            flex={1}
          >
            {initialData ? "به‌روزرسانی" : "ایجاد"}
          </Button>
          <Button onClick={onCancel} variant="outline" flex={1}>
            انصراف
          </Button>
        </HStack>
      </VStack>
    </form>
  );
};
